"use client"

import {
  Card,
  CardContent,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  Badge,
  UpgradeNotice,
  SMART_ANALYSIS_RESULTS_PAGE_SIZE,
  excludeProfileIds,
  topRankedCandidates,
  groupCandidatesByTier,
  tierCounts,
  PaneResponsiveCardGrid,
  PaneListCount,
  ListSortMenu,
  EVALUATION_RESULT_SORT_OPTIONS,
  parseEvaluationResultSort,
  sortEvaluationResults,
  Skeleton,
  applicantsHaveMatchScores,
  cn,
} from '@yeshuman/sdk'
import type {
  SmartSearchError,
  UnifiedCandidate,
  OpportunityMatch,
  OpportunitySearchResponse,
  EvaluationResultSort,
  OpenOpportunityMatchHandler,
  OpenPersonMatchHandler,
  SmartAnalysisDirection,
} from '@yeshuman/sdk'
import { type ReactNode, useEffect, useState } from 'react'
import { UnifiedCandidateCard } from '../recruiter/unified-candidate-card'
import { UnifiedOpportunityCard } from './unified-opportunity-card'
import { SearchProgressCollapsedSummary } from './search-progress-collapsed-summary'
import { SmartAnalysisRunPanel } from './smart-analysis-run-panel'
import { EvaluationSummarySection } from './evaluation-summary-section'
import { SmartAnalysisIntroCard } from './smart-analysis-intro-card'
import {
  SmartAnalysisTierDigest,
  SmartAnalysisTierSection,
} from './smart-analysis-tiered-results'
import { SmartAnalysisOutcomeSummary } from './smart-analysis-outcome-summary'
import { SmartAnalysisLeaderboard } from './smart-analysis-leaderboard'
import { SmartAnalysisResultsPagination } from './smart-analysis-results-pagination'
import type { SearchCandidatesResponse } from '../../hooks/use-briefs'
import type { SearchProgressStage } from '../recruiter/search-progress'
import { motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import type { OpenJobWorkspacePersonHandler } from '../../lib/job-workspace-person-paths'
import type { ApplicantProfileSummary, ApplicantCard } from '../../hooks/use-opportunity-applicants'
import type { JobAnalysisCoverage } from '../../hooks/use-job-analysis-coverage'
import type { JobProspectMatch } from '../../hooks/use-job-prospect-matches'
import { SmartAnalysisJobReport } from './smart-analysis-job-report'
import { FitRetryStatus, resolveFitRetryState } from './analysis-retry-button'

type AppliedFilter = 'all' | 'applied' | 'not_applied'

export type SmartAnalysisResultsPresentation = 'default' | 'leaderboard'

export type SmartAnalysisLayoutVariant = 'standalone' | 'job-report'

export type SmartAnalysisJobReportConfig = {
  applicants: ApplicantCard[]
  prospectMatches: JobProspectMatch[]
  coverage?: JobAnalysisCoverage | null
  onReviewPipeline: () => void
  onReviewInvite: () => void
  onViewInPipeline: (profileId: string) => void
  onNudge?: (applicationId: string) => void
  nudgePending?: boolean
  onRetryApplicant?: (applicationId: string) => void
  retryPendingApplicationId?: string | null
  pendingApplicationIds?: ReadonlySet<string>
  stuckApplicationIds?: ReadonlySet<string>
  readOnly?: boolean
}

type SmartAnalysisResultsShellBaseProps = {
  stages: SearchProgressStage[]
  isSearching: boolean
  isHydrating?: boolean
  searchError: Error | null
  strategyProgress?: Record<string, { candidatesFound: number; totalProcessed?: number; isComplete?: boolean }>
  parallelOperations?: number
  processingSpeed?: number
  appliedFilter?: AppliedFilter
  onAppliedFilterChange?: (value: AppliedFilter) => void
  appliedIdsMap?: Set<string>
  showCount?: number
  onShowMore?: () => void
  /** Expand ranked results to the full list (employer Analysis pagination). */
  onShowAll?: () => void
  toolbarStart?: ReactNode
  searchSummary?: string | null
  introInstructions?: string
  onIntroInstructionsChange?: (value: string) => void
  introPreferencesLeadingSlot?: ReactNode
  onRunSmartAnalysis?: () => void
  runDisabled?: boolean
  introJobTitle?: string
  introJobScoped?: boolean
  introEmptyActiveApplicants?: boolean
  analysisLastUpdatedAt?: string | null
  onOpenPerson?: OpenJobWorkspacePersonHandler | OpenPersonMatchHandler
  onOpenMatch?: OpenOpportunityMatchHandler
  /** Shared brief: open a person only when their write-up is on the snapshot. */
  openOnlyWithAnalysis?: boolean
  /** Job-scoped applicant profiles keyed by profile id (Analysis tab). */
  applicantProfileById?: Record<string, ApplicantProfileSummary>
  /** Employer job Analysis: outcome summary, segmented bar, and top-3 leaderboard. */
  presentation?: SmartAnalysisResultsPresentation
  layoutVariant?: SmartAnalysisLayoutVariant
  jobReport?: SmartAnalysisJobReportConfig
  /** True when a live job-scoped run finished in this visit (not a hydrated orphan session). */
  jobReportLiveComplete?: boolean
}

export type SmartAnalysisResultsShellProps =
  | (SmartAnalysisResultsShellBaseProps & {
      direction: 'candidates'
      liveItems: UnifiedCandidate[]
      results: SearchCandidatesResponse | null
      autoSaveBriefId?: string
      renderCandidateAction?: (candidate: UnifiedCandidate) => ReactNode
    })
  | (SmartAnalysisResultsShellBaseProps & {
      direction: 'opportunities'
      liveItems: OpportunityMatch[]
      results: OpportunitySearchResponse | null
      renderOpportunityAction?: (opportunity: OpportunityMatch) => ReactNode
    })

function ShellFrame({
  toolbarStart,
  runPanel,
  children,
}: {
  toolbarStart?: ReactNode
  runPanel?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="space-y-4">
      {toolbarStart}
      {runPanel}
      {children}
    </div>
  )
}

function resolveShowIntro({
  isSearching,
  rawCount,
  hasCompletedSession,
  onRunSmartAnalysis,
}: {
  isSearching: boolean
  rawCount: number
  hasCompletedSession: boolean
  onRunSmartAnalysis?: () => void
}) {
  if (!onRunSmartAnalysis || isSearching) return false
  const isZeroResultsRun = hasCompletedSession && rawCount === 0
  return rawCount === 0 && (!hasCompletedSession || isZeroResultsRun)
}

function smartAnalysisListMeta(
  llmJudgedCount?: number | null,
  isSearching?: boolean,
): string[] | undefined {
  const meta: string[] = []
  if (llmJudgedCount != null && llmJudgedCount > 0) {
    meta.push(`${llmJudgedCount} LLM evaluated`)
  }
  if (isSearching) {
    meta.push('Updating analysis…')
  }
  return meta.length > 0 ? meta : undefined
}

export function SmartAnalysisResultsShell(props: SmartAnalysisResultsShellProps) {
  const {
    direction,
    stages,
    isSearching,
    isHydrating = false,
    searchError,
    strategyProgress = {},
    parallelOperations,
    processingSpeed,
    appliedFilter = 'all',
    onAppliedFilterChange,
    appliedIdsMap,
    showCount,
    onShowMore,
    onShowAll,
    toolbarStart,
    searchSummary,
    introInstructions,
    onIntroInstructionsChange,
    introPreferencesLeadingSlot,
    onRunSmartAnalysis,
    runDisabled,
    introJobTitle,
    introJobScoped,
    introEmptyActiveApplicants,
    analysisLastUpdatedAt,
    onOpenPerson,
    onOpenMatch,
  } = props

  const [sort, setSort] = useState<EvaluationResultSort>('rank_desc')
  const isComplete = !!props.results && !isSearching
  const summaryText = (searchSummary ?? props.results?.search_summary)?.trim()
  const presentation =
    direction === 'candidates'
      ? ((props as Extract<SmartAnalysisResultsShellProps, { direction: 'candidates' }>)
          .presentation ?? 'default')
      : 'default'
  const runPanel = (
    <SmartAnalysisRunPanel
      summary={
        isComplete && presentation !== 'leaderboard' ? summaryText || undefined : undefined
      }
      stages={stages}
      isSearching={isSearching}
      isComplete={isComplete}
      strategyProgress={strategyProgress}
      parallelOperations={parallelOperations}
      processingSpeed={processingSpeed}
    />
  )
  const retrySummaryCard = summaryText ? (
    <EvaluationSummarySection summary={summaryText} />
  ) : null

  if (searchError) {
    const denial = (searchError as SmartSearchError).denial
    if (denial) {
      return (
        <ShellFrame toolbarStart={toolbarStart} runPanel={runPanel}>
          <UpgradeNotice denial={denial} className="mx-0" />
        </ShellFrame>
      )
    }
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error: {searchError.message}</p>
        </CardContent>
      </Card>
    )
  }

  if (isHydrating) {
    return (
      <ShellFrame toolbarStart={toolbarStart} runPanel={runPanel}>
        <Card>
          <CardContent
            className="space-y-3 py-6"
            aria-busy="true"
            aria-live="polite"
            aria-label="Loading analysis results"
          >
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </ShellFrame>
    )
  }

  const sharedBodyProps = {
    isComplete,
    runPanel,
    retrySummaryCard,
    appliedFilter,
    onAppliedFilterChange,
    appliedIdsMap,
    showCount,
    onShowMore,
    onShowAll,
    toolbarStart,
    stages,
    isSearching,
    strategyProgress,
    parallelOperations,
    processingSpeed,
    introInstructions,
    onIntroInstructionsChange,
    introPreferencesLeadingSlot,
    onRunSmartAnalysis,
    runDisabled,
    introJobTitle,
    introJobScoped,
    introEmptyActiveApplicants,
    analysisLastUpdatedAt,
    onOpenPerson,
    onOpenMatch,
  }

  if (direction === 'candidates') {
    return (
      <CandidateResultsBody
        {...props}
        {...sharedBodyProps}
        sort={sort}
        setSort={setSort}
      />
    )
  }

  return <OpportunityResultsBody {...props} {...sharedBodyProps} />
}

type SharedBodyProps = {
  isComplete: boolean
  runPanel: ReactNode
  retrySummaryCard: ReactNode
  appliedFilter: AppliedFilter
  onAppliedFilterChange?: (value: AppliedFilter) => void
  appliedIdsMap?: Set<string>
  showCount?: number
  onShowMore?: () => void
  onShowAll?: () => void
  toolbarStart?: ReactNode
  stages: SearchProgressStage[]
  isSearching: boolean
  strategyProgress: Record<string, { candidatesFound: number; totalProcessed?: number; isComplete?: boolean }>
  parallelOperations?: number
  processingSpeed?: number
  introInstructions?: string
  onIntroInstructionsChange?: (value: string) => void
  introPreferencesLeadingSlot?: ReactNode
  onRunSmartAnalysis?: () => void
  runDisabled?: boolean
  introJobTitle?: string
  introJobScoped?: boolean
  introEmptyActiveApplicants?: boolean
  analysisLastUpdatedAt?: string | null
}

type CandidateBodyProps = Extract<SmartAnalysisResultsShellProps, { direction: 'candidates' }> &
  SharedBodyProps & {
    sort: EvaluationResultSort
    setSort: (value: EvaluationResultSort) => void
  }

function SmartAnalysisIntroPanel({
  direction,
  mode,
  retrySummaryCard,
  isSearching,
  isComplete,
  candidateResults,
  opportunityResults,
  introInstructions,
  onIntroInstructionsChange,
  introPreferencesLeadingSlot,
  introJobTitle,
  introJobScoped,
  introEmptyActiveApplicants,
  analysisLastUpdatedAt,
  onRunSmartAnalysis,
  runDisabled,
}: {
  direction: SmartAnalysisDirection
  mode: 'idle' | 'retry'
  retrySummaryCard: ReactNode
  isComplete: boolean
  isSearching: boolean
  candidateResults?: SearchCandidatesResponse | null
  opportunityResults?: OpportunitySearchResponse | null
  introInstructions: string
  onIntroInstructionsChange?: (value: string) => void
  introPreferencesLeadingSlot?: ReactNode
  introJobTitle?: string
  introJobScoped?: boolean
  introEmptyActiveApplicants?: boolean
  analysisLastUpdatedAt?: string | null
  onRunSmartAnalysis?: () => void
  runDisabled?: boolean
}) {
  if (!onRunSmartAnalysis || !onIntroInstructionsChange) {
    return null
  }

  return (
    <ShellFrame runPanel={mode === 'retry' ? retrySummaryCard : null}>
      {mode === 'retry' && isComplete ? (
        <SearchProgressCollapsedSummary
          direction={direction}
          resultsSummary={candidateResults ?? opportunityResults ?? undefined}
        />
      ) : null}
      <SmartAnalysisIntroCard
        direction={direction}
        mode={mode}
        jobTitle={introJobTitle}
        jobScoped={introJobScoped}
        emptyActiveApplicants={introEmptyActiveApplicants}
        instructions={introInstructions}
        onInstructionsChange={onIntroInstructionsChange}
        preferencesLeadingSlot={introPreferencesLeadingSlot}
        onRun={onRunSmartAnalysis}
        isRunning={isSearching}
        runDisabled={runDisabled}
        lastUpdatedAt={analysisLastUpdatedAt}
      />
    </ShellFrame>
  )
}

function CandidateResultsBody({
  liveItems: candidates,
  results,
  autoSaveBriefId,
  renderCandidateAction,
  isComplete,
  runPanel,
  retrySummaryCard,
  sort,
  setSort,
  appliedFilter,
  onAppliedFilterChange,
  appliedIdsMap,
  showCount,
  onShowMore,
  onShowAll,
  toolbarStart,
  stages,
  isSearching,
  introInstructions,
  onIntroInstructionsChange,
  introPreferencesLeadingSlot,
  onRunSmartAnalysis,
  runDisabled,
  introJobTitle,
  introJobScoped,
  introEmptyActiveApplicants,
  analysisLastUpdatedAt,
  onOpenPerson,
  openOnlyWithAnalysis = false,
  applicantProfileById,
  searchSummary,
  presentation = 'default',
  layoutVariant = 'standalone',
  jobReport,
  jobReportLiveComplete = false,
}: CandidateBodyProps) {
  const [precludedShowCount, setPrecludedShowCount] = useState(SMART_ANALYSIS_RESULTS_PAGE_SIZE)

  useEffect(() => {
    setPrecludedShowCount(SMART_ANALYSIS_RESULTS_PAGE_SIZE)
  }, [results?.session_id, appliedFilter])

  const isJobReport = layoutVariant === 'job-report'

  // While a job-scoped run streams, progress replaces the report — skip the
  // filter/sort work below since its output would be discarded.
  if (isJobReport && jobReport && isSearching) {
    return <ShellFrame runPanel={runPanel}>{null}</ShellFrame>
  }

  let displayCandidates: UnifiedCandidate[] =
    candidates.length > 0
      ? candidates
      : (results?.top_matches?.map((m) => ({
          profile_id: m.profile_id,
          name: m.candidate_name,
          score: m.final_score,
          state: 'analyzed' as const,
          rank: m.rank,
          full: m,
        })) ?? [])

  if (appliedIdsMap && appliedFilter !== 'all') {
    displayCandidates = displayCandidates.filter((c) =>
      appliedFilter === 'applied' ? appliedIdsMap.has(c.profile_id) : !appliedIdsMap.has(c.profile_id),
    )
  }

  displayCandidates = sortEvaluationResults(displayCandidates, sort)

  const hasAnalysedScores =
    applicantsHaveMatchScores(jobReport?.applicants ?? []) || jobReportLiveComplete
  const rawCandidateCount = candidates.length || results?.top_matches?.length || 0
  const hasCompletedSession = Boolean(results?.session_id) && !isSearching
  const showIntro =
    !isJobReport &&
    resolveShowIntro({
      isSearching,
      rawCount: rawCandidateCount,
      hasCompletedSession,
      onRunSmartAnalysis,
    })
  const visibleToolbar = showIntro ? undefined : toolbarStart

  if (isJobReport && jobReport) {
    if (!hasAnalysedScores) {
      if (!onRunSmartAnalysis) {
        if (introEmptyActiveApplicants) {
          return (
            <ShellFrame>
              <p className="text-sm text-muted-foreground">
                No applicants or invited candidates yet. When people apply or are invited, they
                appear here with match scores.
              </p>
            </ShellFrame>
          )
        }
        return (
          <ShellFrame>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Match scores appear as candidates apply or are invited. Try again on anyone who
                is still waiting.
              </p>
              <ul className="space-y-2">
                {jobReport.applicants.map((applicant) => {
                  const status = resolveFitRetryState(
                    jobReport.pendingApplicationIds?.has(applicant.id) ?? false,
                    jobReport.stuckApplicationIds?.has(applicant.id) ?? false,
                  )
                  return (
                    <li
                      key={applicant.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2"
                    >
                      {jobReport.readOnly ? (
                        <span className="text-sm font-medium">{applicant.profile.name}</span>
                      ) : (
                        <Button
                          type="button"
                          variant="link"
                          size="inline"
                          onClick={() =>
                            onOpenPerson?.(
                              applicant.profile.id,
                              applicant.profile.name,
                              applicant.evaluation_id ?? undefined,
                            )
                          }
                        >
                          {applicant.profile.name}
                        </Button>
                      )}
                      {jobReport.readOnly ? null : (
                        <FitRetryStatus
                          tone="employer"
                          state={status}
                          onRetry={
                            jobReport.onRetryApplicant
                              ? () => jobReport.onRetryApplicant?.(applicant.id)
                              : undefined
                          }
                          retryPending={jobReport.retryPendingApplicationId === applicant.id}
                        />
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          </ShellFrame>
        )
      }
      return (
        <SmartAnalysisIntroPanel
          direction="candidates"
          mode="idle"
          retrySummaryCard={retrySummaryCard}
          isSearching={isSearching}
          isComplete={isComplete}
          candidateResults={results}
          introInstructions={introInstructions ?? ''}
          onIntroInstructionsChange={onIntroInstructionsChange}
          introPreferencesLeadingSlot={introPreferencesLeadingSlot}
          introJobTitle={introJobTitle}
          introJobScoped={introJobScoped}
          introEmptyActiveApplicants={introEmptyActiveApplicants}
          analysisLastUpdatedAt={undefined}
          onRunSmartAnalysis={onRunSmartAnalysis}
          runDisabled={runDisabled}
        />
      )
    }

    return (
      <ShellFrame toolbarStart={toolbarStart} runPanel={undefined}>
        <SmartAnalysisJobReport
          applicants={jobReport.applicants}
          candidates={displayCandidates}
          prospectMatches={jobReport.prospectMatches}
          coverage={jobReport.coverage}
          applicantProfileById={applicantProfileById}
          searchSummary={(searchSummary ?? results?.search_summary)?.trim() || null}
          llmJudgedCount={results?.llm_judged_count}
          onReviewPipeline={jobReport.onReviewPipeline}
          onReviewInvite={jobReport.onReviewInvite}
          onRunAnalysis={onRunSmartAnalysis}
          onViewInPipeline={jobReport.onViewInPipeline}
          onOpenPerson={onOpenPerson}
          onNudge={jobReport.readOnly ? undefined : jobReport.onNudge}
          nudgePending={jobReport.nudgePending}
          readOnly={jobReport.readOnly}
          previousRunAt={analysisLastUpdatedAt ?? results?.previous_run_at ?? null}
        />
      </ShellFrame>
    )
  }

  if (showIntro) {
    return (
      <SmartAnalysisIntroPanel
        direction="candidates"
        mode={hasCompletedSession ? 'retry' : 'idle'}
        retrySummaryCard={retrySummaryCard}
        isSearching={isSearching}
        isComplete={isComplete}
        candidateResults={results}
        introInstructions={introInstructions ?? ''}
        onIntroInstructionsChange={onIntroInstructionsChange}
        introPreferencesLeadingSlot={introPreferencesLeadingSlot}
        introJobTitle={introJobTitle}
        introJobScoped={introJobScoped}
        introEmptyActiveApplicants={introEmptyActiveApplicants}
        analysisLastUpdatedAt={analysisLastUpdatedAt}
        onRunSmartAnalysis={onRunSmartAnalysis}
        runDisabled={runDisabled}
      />
    )
  }

  const hasContent =
    rawCandidateCount > 0 || stages.length > 0 || isSearching || hasCompletedSession

  if (!hasContent && !isSearching) {
    return null
  }

  // Group into result tiers (sort order is preserved within each tier).
  const tierGroups = groupCandidatesByTier(displayCandidates)
  const counts = tierCounts(tierGroups)
  const isLeaderboard = presentation === 'leaderboard'
  const topThree = isLeaderboard ? topRankedCandidates(displayCandidates) : []
  const topThreeIds = new Set(topThree.map((c) => c.profile_id))
  const remainingTierGroups = isLeaderboard
    ? {
        top: excludeProfileIds(tierGroups.top, topThreeIds),
        compelling: excludeProfileIds(tierGroups.compelling, topThreeIds),
        precluded: excludeProfileIds(tierGroups.precluded, topThreeIds),
      }
    : tierGroups
  // "Show more" paginates top + compelling tiers; precluded uses its own page size.
  const rankedItems = [...remainingTierGroups.top, ...remainingTierGroups.compelling]
  const resultsPaginated =
    showCount != null && (onShowMore != null || onShowAll != null)
  const effectiveShowCount = showCount ?? rankedItems.length
  const visibleRanked = resultsPaginated
    ? rankedItems.slice(0, effectiveShowCount)
    : rankedItems
  const visibleTop = visibleRanked.slice(0, remainingTierGroups.top.length)
  const visibleCompelling = visibleRanked.slice(remainingTierGroups.top.length)
  const visiblePrecluded = remainingTierGroups.precluded.slice(0, precludedShowCount)
  const outcomeSummaryText = (searchSummary ?? results?.search_summary)?.trim()

  return (
    <ShellFrame toolbarStart={visibleToolbar} runPanel={runPanel}>
      {rawCandidateCount > 0 && (
        <div className={cn('space-y-3', isSearching && 'opacity-70')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <PaneListCount
                count={displayCandidates.length}
                unit={{ singular: 'candidate', plural: 'candidates' }}
                meta={smartAnalysisListMeta(results?.llm_judged_count, isSearching)}
              />
              {isSearching ? (
                <Badge variant="secondary" className="font-normal">
                  Previous run
                </Badge>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {onAppliedFilterChange && appliedIdsMap && (
                <Tabs
                  value={appliedFilter}
                  onValueChange={(value) => onAppliedFilterChange(value as AppliedFilter)}
                >
                  <TabsList aria-label="Filter analysis results by application status">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="applied">Applied</TabsTrigger>
                    <TabsTrigger value="not_applied">Not Applied</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
              {displayCandidates.length > 0 ? (
                <ListSortMenu
                  options={EVALUATION_RESULT_SORT_OPTIONS}
                  value={sort}
                  onValueChange={(value) => setSort(parseEvaluationResultSort(value))}
                  ariaLabel="Sort evaluation results"
                />
              ) : null}
            </div>
          </div>
          {displayCandidates.length === 0 ? (
            <p className="py-6 text-center text-muted-foreground">No candidates match this filter</p>
          ) : (
            <>
              {isLeaderboard ? (
                <>
                  <SmartAnalysisOutcomeSummary
                    summary={outcomeSummaryText}
                    counts={counts}
                    totalCount={displayCandidates.length}
                    llmJudgedCount={results?.llm_judged_count}
                  />
                  <SmartAnalysisLeaderboard
                    candidates={topThree}
                    onOpenPerson={onOpenPerson}
                    applicantProfileById={applicantProfileById}
                    renderCandidateAction={renderCandidateAction}
                    openOnlyWithAnalysis={openOnlyWithAnalysis}
                  />
                </>
              ) : (
                <SmartAnalysisTierDigest counts={counts} />
              )}
              <SmartAnalysisTierSection
                tier="top"
                count={visibleTop.length > 0 ? remainingTierGroups.top.length : 0}
              >
                <CandidateTierGrid
                  candidates={visibleTop}
                  autoSaveBriefId={autoSaveBriefId}
                  introJobScoped={introJobScoped}
                  renderCandidateAction={renderCandidateAction}
                  onOpenPerson={onOpenPerson}
                  openOnlyWithAnalysis={openOnlyWithAnalysis}
                  applicantProfileById={applicantProfileById}
                />
              </SmartAnalysisTierSection>
              <SmartAnalysisTierSection
                tier="compelling"
                count={
                  visibleCompelling.length > 0 ? remainingTierGroups.compelling.length : 0
                }
              >
                <CandidateTierGrid
                  candidates={visibleCompelling}
                  autoSaveBriefId={autoSaveBriefId}
                  introJobScoped={introJobScoped}
                  renderCandidateAction={renderCandidateAction}
                  onOpenPerson={onOpenPerson}
                  openOnlyWithAnalysis={openOnlyWithAnalysis}
                  applicantProfileById={applicantProfileById}
                />
              </SmartAnalysisTierSection>
              <SmartAnalysisResultsPagination
                visibleCount={visibleRanked.length}
                totalCount={rankedItems.length}
                unit={{ singular: 'candidate', plural: 'candidates' }}
                onShowMore={onShowMore}
                onShowAll={onShowAll}
              />
              <SmartAnalysisTierSection
                tier="precluded"
                count={remainingTierGroups.precluded.length}
                collapsible
              >
                <CandidateTierGrid
                  candidates={visiblePrecluded}
                  autoSaveBriefId={autoSaveBriefId}
                  introJobScoped={introJobScoped}
                  renderCandidateAction={renderCandidateAction}
                  onOpenPerson={onOpenPerson}
                  openOnlyWithAnalysis={openOnlyWithAnalysis}
                  applicantProfileById={applicantProfileById}
                />
                <SmartAnalysisResultsPagination
                  visibleCount={visiblePrecluded.length}
                  totalCount={remainingTierGroups.precluded.length}
                  unit={{ singular: 'candidate', plural: 'candidates' }}
                  onShowMore={() =>
                    setPrecludedShowCount((prev) =>
                      Math.min(
                        prev + SMART_ANALYSIS_RESULTS_PAGE_SIZE,
                        remainingTierGroups.precluded.length,
                      ),
                    )
                  }
                  onShowAll={() =>
                    setPrecludedShowCount(remainingTierGroups.precluded.length)
                  }
                />
              </SmartAnalysisTierSection>
            </>
          )}
        </div>
      )}
    </ShellFrame>
  )
}

function candidateHasFrozenAnalysis(candidate: UnifiedCandidate): boolean {
  const full = candidate.full as { analysis?: unknown } | undefined
  return Boolean(full?.analysis && typeof full.analysis === 'object')
}

function CandidateTierGrid({
  candidates,
  autoSaveBriefId,
  introJobScoped,
  renderCandidateAction,
  onOpenPerson,
  openOnlyWithAnalysis = false,
  applicantProfileById,
}: {
  candidates: UnifiedCandidate[]
  autoSaveBriefId?: string
  introJobScoped?: boolean
  renderCandidateAction?: (candidate: UnifiedCandidate) => ReactNode
  onOpenPerson?: OpenJobWorkspacePersonHandler | OpenPersonMatchHandler
  openOnlyWithAnalysis?: boolean
  applicantProfileById?: Record<string, ApplicantProfileSummary>
}) {
  if (candidates.length === 0) return null
  return (
    <PaneResponsiveCardGrid>
      {candidates.map((candidate) => (
        <motion.div
          key={candidate.profile_id}
          layout
          className="flex min-h-0 min-w-0 flex-col [&>*]:flex-1"
        >
          <UnifiedCandidateCard
            candidate={candidate}
            autoSaveBriefId={autoSaveBriefId}
            hideSaveButton={!!introJobScoped}
            renderAction={renderCandidateAction}
            onOpenPerson={
              !onOpenPerson || (openOnlyWithAnalysis && !candidateHasFrozenAnalysis(candidate))
                ? undefined
                : onOpenPerson
            }
            profileSummary={applicantProfileById?.[candidate.profile_id]}
          />
        </motion.div>
      ))}
    </PaneResponsiveCardGrid>
  )
}

type OpportunityBodyProps = Extract<SmartAnalysisResultsShellProps, { direction: 'opportunities' }> &
  SharedBodyProps

function OpportunityResultsBody({
  liveItems: opportunities,
  results,
  renderOpportunityAction,
  isComplete,
  runPanel,
  retrySummaryCard,
  appliedFilter,
  onAppliedFilterChange,
  appliedIdsMap,
  showCount,
  onShowMore,
  toolbarStart,
  stages,
  isSearching,
  introInstructions,
  onIntroInstructionsChange,
  introPreferencesLeadingSlot,
  onRunSmartAnalysis,
  runDisabled,
  analysisLastUpdatedAt,
  onOpenMatch,
}: OpportunityBodyProps) {
  let displayOpportunities: OpportunityMatch[] =
    opportunities.length > 0 ? opportunities : (results?.top_matches ?? [])

  if (appliedIdsMap && appliedFilter !== 'all') {
    displayOpportunities = displayOpportunities.filter((o) =>
      appliedFilter === 'applied' ? appliedIdsMap.has(o.opportunity_id) : !appliedIdsMap.has(o.opportunity_id),
    )
  }

  const visibleOpportunities =
    showCount != null && onShowMore ? displayOpportunities.slice(0, showCount) : displayOpportunities
  const hasMore = showCount != null && onShowMore && displayOpportunities.length > showCount
  const rawOpportunityCount = opportunities.length || results?.top_matches?.length || 0
  const hasCompletedSession = Boolean(results?.session_id) && !isSearching
  const showIntro = resolveShowIntro({
    isSearching,
    rawCount: rawOpportunityCount,
    hasCompletedSession,
    onRunSmartAnalysis,
  })
  const visibleToolbar = showIntro ? undefined : toolbarStart

  if (showIntro) {
    return (
      <SmartAnalysisIntroPanel
        direction="opportunities"
        mode={hasCompletedSession ? 'retry' : 'idle'}
        retrySummaryCard={retrySummaryCard}
        isSearching={isSearching}
        isComplete={isComplete}
        opportunityResults={results}
        introInstructions={introInstructions ?? ''}
        onIntroInstructionsChange={onIntroInstructionsChange}
        introPreferencesLeadingSlot={introPreferencesLeadingSlot}
        analysisLastUpdatedAt={analysisLastUpdatedAt}
        onRunSmartAnalysis={onRunSmartAnalysis}
        runDisabled={runDisabled}
      />
    )
  }

  const hasContent =
    rawOpportunityCount > 0 || stages.length > 0 || isSearching || hasCompletedSession

  if (!hasContent && !isSearching) {
    return null
  }

  return (
    <ShellFrame toolbarStart={visibleToolbar} runPanel={runPanel}>
      {rawOpportunityCount > 0 && (
        <div className={cn('space-y-3', isSearching && 'opacity-70')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <PaneListCount
                count={displayOpportunities.length}
                unit={{ singular: 'opportunity', plural: 'opportunities' }}
                meta={smartAnalysisListMeta(results?.llm_judged_count, isSearching)}
              />
              {isSearching ? (
                <Badge variant="secondary" className="font-normal">
                  Previous run
                </Badge>
              ) : null}
            </div>
            {onAppliedFilterChange && appliedIdsMap && (
              <Tabs
                value={appliedFilter}
                onValueChange={(value) => onAppliedFilterChange(value as AppliedFilter)}
              >
                <TabsList aria-label="Filter analysis results by application status">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="applied">Applied</TabsTrigger>
                  <TabsTrigger value="not_applied">Not Applied</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>
          {displayOpportunities.length === 0 ? (
            <p className="py-6 text-center text-muted-foreground">No opportunities match this filter</p>
          ) : (
            <PaneResponsiveCardGrid>
              {visibleOpportunities.map((opportunity) => (
                <motion.div
                  key={opportunity.opportunity_id}
                  layout
                  className="flex min-h-0 min-w-0 flex-col [&>*]:flex-1"
                >
                  <UnifiedOpportunityCard
                    opportunity={opportunity}
                    renderAction={renderOpportunityAction}
                    onOpenMatch={onOpenMatch}
                  />
                </motion.div>
              ))}
            </PaneResponsiveCardGrid>
          )}
          {hasMore && onShowMore && (
            <div className="pt-2 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowMore}
                className="mx-auto flex items-center gap-2"
              >
                <ChevronDown className="h-4 w-4" />
                Show {Math.min(10, displayOpportunities.length - (showCount ?? 0))} more opportunities
              </Button>
            </div>
          )}
        </div>
      )}
    </ShellFrame>
  )
}
